<template>
    <div
        v-if="show"
        :class="[
            classWrapper,
            'custom-modal relative',
            {
                'is-slide-modal z-[55] lg:hidden': type === types.slideModal,
                'is-popup-modal z-[80]': type === types.popupModal,
            },
        ]"
    >
        <div
            v-if="type === types.popupModal"
            class="custom-modal-backdrop pointer-events-none fixed inset-0 z-[1002] overscroll-none bg-black bg-opacity-70 transition-opacity"
        ></div>

        <div
            :class="[
                'custom-modal-content fixed inset-0 overflow-y-auto',
                {
                    'height-slide-modal z-[1001]': type === types.slideModal,
                    'z-[1002]': type === types.popupModal,
                },
            ]"
        >
            <div
                :class="[
                    'flex justify-center sm:p-0 lg:items-center lg:p-4',
                    {
                        'animate-slide-right height-slide-modal':
                            type === types.slideModal && isAnimationSlideModal,
                        'height-slide-modal': type === types.slideModal,
                        'animate-slide-up h-full items-end lg:items-center':
                            type === types.popupModal,
                    },
                ]"
            >
                <div
                    ref="ModalWindow"
                    :class="[
                        classes,
                        'relative w-full transform overflow-hidden bg-white transition-all lg:my-8',
                        {
                            'sm:max-w-sm':
                                size === 'sm' && type === types.popupModal,
                            'sm:max-w-md':
                                size === 'md' && type === types.popupModal,
                            'sm:max-w-lg':
                                size === 'lg' && type === types.popupModal,
                            'sm:max-w-xl':
                                size === 'xl' && type === types.popupModal,
                            'sm:max-w-2xl':
                                size === 'xxl' && type === types.popupModal,
                            'rounded-t-3xl lg:rounded-2xl':
                                type === types.popupModal,
                        },
                    ]"
                >
                    <div
                        :class="[
                            'custom-modal-header relative text-center',
                            classHeaderWrapper,
                            {
                                'border-b border-b-gray-200':
                                    type === types.slideModal,
                            },
                        ]"
                    >
                        <div
                            v-if="$slots['header'] || type === types.slideModal"
                            :class="[
                                'relative bg-white text-center',
                                {
                                    'flex items-end justify-center px-4 pb-0 pt-5 lg:px-8 lg:pt-8':
                                        type === types.popupModal,
                                    'px-3 py-4': type === types.slideModal,
                                },
                            ]"
                        >
                            <V2IconsChevronLeft
                                v-if="type === types.slideModal"
                                class-wrapper="w5 h5 absolute left-3 top-4"
                                fill-color="fill-grey-900"
                                @click="emit('close')"
                            />
                            <h2
                                v-if="type === types.slideModal"
                                class="custom-modal-title text-base font-semibold capitalize text-grey-900"
                            >
                                {{ $t(title) }}
                            </h2>
                            <slot name="header"></slot>
                        </div>
                        <div
                            v-if="!hideClose && type === types.popupModal"
                            class="absolute right-3 top-3 h-[30px] w-[30px] cursor-pointer"
                            @click="emit('close')"
                        >
                            <img
                                :src="`${staticUrl}/assets/icons/close.svg`"
                                alt="close"
                            />
                        </div>
                    </div>
                    <div
                        :class="[
                            'custom-modal-body',
                            classContentWrapper,
                            {
                                'pt-5 lg:pt-6':
                                    !!$slots['header'] &&
                                    type === types.popupModal,
                                'pt-0':
                                    !$slots['header'] &&
                                    type === types.popupModal,
                                'pb-5 lg:pb-6': !!$slots['footer'],
                                'pb-6 lg:pb-8':
                                    !$slots['footer'] &&
                                    type === types.popupModal,
                                'px-4 lg:px-8': type === types.popupModal,
                                'px-3 pt-4': type === types.slideModal,
                            },
                        ]"
                    >
                        <slot></slot>
                    </div>
                    <div
                        v-if="$slots['footer']"
                        :class="`custom-modal-footer flex flex-col gap-5 px-4 pb-5 pt-0 lg:gap-6 lg:px-8 lg:pb-8 ${classFooterrWrapper}`"
                    >
                        <slot name="footer"></slot>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script setup>
import { onClickOutside } from '@vueuse/core'
const staticUrl = useRuntimeConfig().public.staticUrl
const { show, sticky, type } = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
    title: {
        type: String,
        default: '',
    },
    hideClose: {
        type: Boolean,
        default: false,
    },
    sticky: {
        type: Boolean,
        default: false,
    },
    size: {
        type: String,
        default: 'lg',
    },
    classes: {
        type: String,
        default: '',
    },
    type: {
        type: String,
        default: 'popup-modal', // popup-modal, slide-modal
    },
    classContentWrapper: {
        type: String,
        default: '',
    },
    classHeaderWrapper: {
        type: String,
        default: '',
    },
    classFooterrWrapper: {
        type: String,
        default: '',
    },
    classWrapper: {
        type: String,
        default: '',
    },
})

const types = {
    popupModal: 'popup-modal',
    slideModal: 'slide-modal',
}
const route = useRoute()
const emit = defineEmits(['close'])
const ModalWindow = ref(null)

const isAnimationSlideModal = computed(() => {
    return !['/account/transaction-history', '/account/bet-history'].includes(
        route.path
    )
})

onClickOutside(ModalWindow, () => {
    if (!sticky && type === types.popupModal) {
        emit('close')
    }
})
</script>
<style lang="scss" scoped>
.animate-slide-up {
    transform: translateY(100%);
    animation: slide-up 0.3s forwards;
    @media screen and (min-width: 1023px) {
        transform: none;
        animation: none;
    }
}

.animate-slide-right {
    transform: translateX(100%);
    animation: slide-right 0.3s forwards;
    @media screen and (min-width: 1023px) {
        transform: none;
        animation: none;
    }
}

.is-slide-modal .custom-modal-body {
    height: calc(100vh - 68px - 57px);
    padding-bottom: 140px;
    overflow-y: auto;
    overflow-x: hidden;
}

.height-slide-modal {
    // height: calc(100vh - 68px);
    margin-bottom: 68px;
    overflow: hidden;
}

.notification-modal {
    &.is-slide-modal {
        .custom-modal-body {
            height: calc(100vh - 57px);
            padding-bottom: 90px;
        }
    }
    .height-slide-modal {
        margin-bottom: 0;
    }
}

@keyframes slide-up {
    0% {
        transform: translateY(100%);
    }
    100% {
        transform: translateY(0);
    }
}

@keyframes slide-right {
    0% {
        transform: translateX(100%);
    }
    100% {
        transform: translateX(0);
    }
}
</style>
